import type { printOnDemand } from "common/utils/models";
import { formatCurrency } from "@design-lab/shared/lib/currency";
import { useEffect, useState } from "react";
import { useCurrentComposition } from "@design-lab/entities/composition";
import type { BasePrice } from "./calculateBasePrice";

interface PriceDisplayProps {
  isSample?: boolean;
  basePriceSizes: Array<{
    start: printOnDemand.getProduct.Size;
    end: printOnDemand.getProduct.Size;
  }>;
  basePrice: BasePrice;
  selectedSizes?: Record<string, number>;
  getVariantPrice?: (variantId: string) => string | undefined;
}

export function PriceDisplay({
  basePriceSizes,
  basePrice,
  isSample,
  selectedSizes,
  getVariantPrice
}: PriceDisplayProps) {
  const [samplePriceData, setSamplePriceData] = useState<{ label: string; price: string } | null>(
    null
  );

  const { currentComposition: { variants } = {} } = useCurrentComposition();

  const getPriceLabel = () => {
    const sizeRanges = basePriceSizes
      .map(size => (size.start === size.end ? size.start : `${size.start}-${size.end}`))
      .join(", ");

    return `Prices for ${sizeRanges}`;
  };

  useEffect(() => {
    if (!isSample || !selectedSizes || !variants || !getVariantPrice) {
      setSamplePriceData(null);
      return;
    }

    const selectedSizeKeys = Object.keys(selectedSizes);
    if (selectedSizeKeys.length === 0) {
      setSamplePriceData(null);
      return;
    }

    const size = selectedSizeKeys[0];
    const variant = variants.find(v => v.size === size);
    const price = variant ? getVariantPrice(variant.id) : null;

    setSamplePriceData(price ? { label: `Price for ${size}`, price } : null);
  }, [variants, selectedSizes, getVariantPrice, isSample]);

  return (
    <p className="text-xs text-gray-500">
      {isSample && samplePriceData ? (
        (() => (
          <>
            {samplePriceData.label}:{" "}
            <span className="text-primary font-semibold">{samplePriceData.price}</span>
          </>
        ))()
      ) : (
        <>
          {getPriceLabel()}:{" "}
          <span className="text-primary font-semibold">{formatCurrency(basePrice.value)}</span>
        </>
      )}
    </p>
  );
}
